import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import {
	DomSanitizer,
	SafeHtml,
	SafeResourceUrl,
	SafeScript,
	SafeStyle,
	SafeUrl,
} from '@angular/platform-browser';
import parsePhoneNumberFromString, { AsYouType } from 'libphonenumber-js';
import { DateTime } from 'luxon';
import { Address } from 'src/app/models/data/address';
import { User } from 'src/app/models/data/user';
import { ControlAssignment, DeviceControl } from '../models/data/deviceControl';
import DeviceAlertType = Components.Schemas.DeviceAlertType;
import { DatePipe } from '@angular/common';

@Pipe({ name: 'utcToLocal' })
export class UtcToLocal implements PipeTransform {
	transform(value: any, ...args: any[]): any {
		const date = DateTime.fromISO(value, { zone: 'UTC' });
		if (!date.isValid) {
			return '';
		}
		const format = { ...DateTime.DATE_MED, ...DateTime.TIME_SIMPLE };

		const siteLocale = window.location.pathname.split('/')[1];
		if (languageList.some((i) => i.code === siteLocale)) {
			/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
			return !!value ? date.setLocale(siteLocale).toLocaleString(format) : '';
		}

		/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
		return !!value ? date.toLocaleString(format) : '';
	}
}

@Pipe({ name: 'accessDateTime' })
export class AccessDateTimePipe implements PipeTransform {
	transform(value: any, ...args: any[]): string | undefined {
		const date = DateTime.fromISO(value, { zone: 'UTC' });

		if (!date.isValid) {
			return '- - -';
		}

		const format = { ...DateTime.DATE_MED, ...DateTime.TIME_SIMPLE };

		const siteLocale = window.location.pathname.split('/')[1];
		if (languageList.some((i) => i.code === siteLocale)) {
			/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
			return !!value
				? date.setLocale(siteLocale).toLocaleString(format)
				: undefined;
		}

		/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
		return !!value ? date.toLocaleString(format) : undefined;
	}
}

@Pipe({ name: 'accessDatePlaceholderPipe' })
export class AccessDatePlaceholderPipe implements PipeTransform {
	private localeFormats: { [key: string]: string } = {
		'en-US': 'MM/DD/YYYY',
		'en-UK': 'DD/MM/YYYY',
		// Add more locales and their corresponding date formats as needed
	};

	transform(placeholder: string = '- - -'): string {
		const siteLocale = window.location.pathname.split('/')[1];
		const dateFormat = this.localeFormats[siteLocale] || placeholder; // Default to input/placeholder format if locale not found
		return placeholder.replace('MM/DD/YYYY', dateFormat);
	}
}

@Pipe({ name: 'shortNamedOffset' })
export class ShortNamedOffsetPipe implements PipeTransform {
	transform(ianaTimeZone: any, ...args: any[]): string | undefined {
		if (!ianaTimeZone) {
			return '';
		}

		const shortNamedOffset = DateTime.fromJSDate(new Date())
			.setZone(ianaTimeZone)
			.toFormat('ZZZZ');

		/* istanbul ignore next: Luxon returns a non-empty offset label for valid and invalid non-empty zones. */
		return !!shortNamedOffset ? shortNamedOffset : '';
	}
}

@Pipe({ name: 'eventDuration' })
export class EventDurationPipe implements PipeTransform {
	transform(duration: string): string | undefined {
		const durationNumber = parseInt(duration, 10);

		if (isNaN(durationNumber)) {
			return '- - -';
		}

		const hours = Math.floor(durationNumber / 60);
		const minutes = durationNumber % 60;
		const hoursText = hours > 0 ? `${hours}h ` : '';
		const minutesText = minutes > 0 ? `${minutes}m` : '';

		return `${hoursText}${minutesText}`;
	}
}

@Pipe({ name: 'accessDate' })
export class AccessDatePipe implements PipeTransform {
	transform(value: any, ...args: any[]): string | undefined {
		const date = DateTime.fromISO(value, { zone: 'UTC' });

		if (!date.isValid) {
			return value;
		}

		const format = { ...DateTime.DATE_MED };

		const siteLocale = window.location.pathname.split('/')[1];
		if (languageList.some((i) => i.code === siteLocale)) {
			/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
			return !!value
				? date.setLocale(siteLocale).toLocaleString(format)
				: undefined;
		}

		/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
		return !!value ? date.toLocaleString(format) : undefined;
	}
}

@Pipe({ name: 'accessTime' })
export class AccessTimePipe implements PipeTransform {
	transform(value: any, ...args: any[]): string | undefined {
		// Handle 24:00:00 time string directly
		if (value === '24:00:00' || value === '24:00') {
			return '24:00';
		}

		const date = DateTime.fromISO(value, { zone: 'UTC' });

		if (!date.isValid) {
			return value;
		}

		const format = { ...DateTime.TIME_24_SIMPLE };

		const siteLocale = window.location.pathname.split('/')[1];
		if (languageList.some((i) => i.code === siteLocale)) {
			/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
			return !!value
				? date.setLocale(siteLocale).toLocaleString(format)
				: undefined;
		}

		/* istanbul ignore next: invalid or falsy values return before this valid-date branch. */
		return !!value ? date.toLocaleString(format) : undefined;
	}
}

@Pipe({
	name: 'accessStartTime',
})
export class AccessStartTimePipe implements PipeTransform {
	transform(value: any, ...args: any[]): string | undefined {
		let date = DateTime.fromISO(value, { zone: 'UTC' });
		let retVal: string;
		if (!date.isValid) {
			return value;
		}

		/* istanbul ignore next: Luxon returns a string for valid HH:mm formatting. */
		retVal = date.toFormat('HH:mm') ?? undefined;

		return retVal;
	}
}

@Pipe({ name: 'accessDateFilter' })
export class AccessDateFilterPipe implements PipeTransform {
	transform(value: any, ...args: any[]): string | undefined {
		if (value instanceof Date) {
			const date = DateTime.fromJSDate(value);
			const format = { ...DateTime.DATE_SHORT };
			return date.toLocaleString(format);
		}

		return value;
	}
}

@Pipe({ name: 'safe' })
export class SafePipe implements PipeTransform {
	// https://medium.com/@swarnakishore/angular-safe-pipe-implementation-to-bypass-domsanitizer-stripping-out-content-c1bf0f1cc36b
	constructor(protected sanitizer: DomSanitizer) {}

	public transform(
		value: any,
		type: string
	): SafeHtml | SafeStyle | SafeScript | SafeUrl | SafeResourceUrl {
		switch (type) {
			case 'html':
				return this.sanitizer.bypassSecurityTrustHtml(value);
			case 'style':
				return this.sanitizer.bypassSecurityTrustStyle(value);
			case 'script':
				return this.sanitizer.bypassSecurityTrustScript(value);
			case 'url':
				return this.sanitizer.bypassSecurityTrustUrl(value);
			case 'resourceUrl':
				return this.sanitizer.bypassSecurityTrustResourceUrl(value);
			default:
				throw new Error(`Invalid safe type specified: ${type}`);
		}
	}
}

@Pipe({ name: 'name' })
export class UserNamePipe implements PipeTransform {
	transform(value: User): SafeHtml {
		return `${value.firstName} ${value.lastName}`;
	}
}

@Pipe({ name: 'emptyData' })
export class EmptyDataPipe implements PipeTransform {
	transform(value: string | undefined): string {
		return !value || value === '' ? '- - -' : value;
	}
}

@Pipe({ name: 'address' })
export class AddressPipe implements PipeTransform {
	constructor(protected sanitizer: DomSanitizer) {}

	transform(address: Address): SafeHtml {
		const siteLocale = window.location.pathname.split('/')[1];

		const address1 = address.address1 ?? '';
		const address2 = address.address2 ?? '';
		const locality = address.locality ?? '';
		const stateProvinceRegion = address.stateProvinceRegion ?? '';
		const postalCode = address.postalCode ?? '';

		let returnStr = '';

		switch (siteLocale) {
			case 'en-US':
				returnStr = `${address1} ${address2}<br>${locality}, ${stateProvinceRegion} ${postalCode}`;
				break;
			case 'en-GB':
				returnStr = `${address1} ${address2}<br>${locality}<br>${postalCode}`;
				break;
			case 'en-ZA':
				returnStr = `${address1} ${address2}<br>${locality}<br>${stateProvinceRegion}<br>${postalCode}`;
				break;
			case 'en-SA':
				returnStr = `${address1} ${address2}<br>${locality} ${postalCode}`;
				break;
			case 'ar-SA':
				returnStr = `${address2} ${address1}<br>${postalCode} ${locality}`;
				break;
			case 'ar-AE':
				returnStr = `${address2} ${address1}<br>${locality} ${stateProvinceRegion}`;
				break;
			case 'bn':
				returnStr = `${address2}<br>${address1}<br>${locality} ${postalCode}`;
				break;
			case 'fil':
				returnStr = `${address1} ${address2}, ${locality}<br>${stateProvinceRegion} ${postalCode}`;
				break;
			case 'fr':
				returnStr = `${address1} ${address2}<br>${postalCode} ${locality} ${stateProvinceRegion}`;
				break;
			case 'de':
				returnStr = `${address1} ${address2}<br>${postalCode} ${locality} ${stateProvinceRegion}`;
				break;
			case 'nl-NL':
			case 'nl-BE':
				returnStr = `${address1} ${address2}<br>${postalCode} ${locality} ${stateProvinceRegion}`;
				break;
			case 'gu':
				returnStr = `${address2}<br>${address1}<br>${locality}<br>${postalCode}<br>${stateProvinceRegion}`;
				break;
			case 'hi':
				returnStr = `${address2}<br>${address1}<br>${locality}<br>${postalCode}<br>${stateProvinceRegion}`;
				break;
			case 'it':
				returnStr = `${address1} ${address2}<br>${postalCode} ${locality} ${stateProvinceRegion}`;
				break;
			case 'pl':
				returnStr = `${address1} ${address2}<br>${postalCode} ${locality}`;
				break;
			case 'pa':
				returnStr = `${address2}<br>${address1}<br>${locality}<br>${postalCode}<br>${stateProvinceRegion}`;
				break;
			case 'es':
				returnStr = `${address1} ${address2}<br>${postalCode} ${locality}`;
				break;
			case 'tr':
				returnStr = `${address1} ${address2}<br>${postalCode} ${locality} ${stateProvinceRegion}`;
				break;
			case 'ur':
				returnStr = `${address1} ${address2}<br>${locality}<br>${postalCode}<br>${stateProvinceRegion}`;
				break;
			case 'cy':
				returnStr = `${address1} ${address2}<br>${locality}<br>${postalCode}`;
				break;
			case 'hu-HU':
				returnStr = `${locality}<br>${address1} ${address2}<br>${postalCode}`;
				break;
			case 'ga-IE':
				returnStr = `${address1} ${address2}<br>${locality}<br>${postalCode}`;
				break;
			case 'lv-LV':
				returnStr = `${address1} ${address2}<br>${locality}<br>${postalCode}`;
				break;
			case 'ro-RO':
				returnStr = `${address1} ${address2}<br>${locality} ${postalCode}`;
				break;

			default:
				returnStr = `${address1} ${address2}<br>${locality}, ${stateProvinceRegion} ${postalCode}`;
		}

		// collapse 2 or more spaces into one
		returnStr = returnStr.replace(/[\s]{2,}/gi, ' ');
		// remove any breaks at start with only space in front
		returnStr = returnStr.replace(/^\s<br>/gi, '');
		// remove any floating breaks
		returnStr = returnStr.replace(/\s<br>\s/gi, '');
		// remove any ending breaks
		returnStr = returnStr.replace(/<br>[\s]*$/gi, '');
		// remove any break commas
		returnStr = returnStr.replace(/<br>,/gi, '');
		// remove any trailing commas with empty space
		returnStr = returnStr.replace(/,[\s]*$/gi, '');

		return returnStr;
	}
}

@Pipe({ name: 'phoneNumber' })
export class PhoneNumberPipe implements PipeTransform {
	constructor(protected sanitizer: DomSanitizer) {}
	transform(phoneNumber: User['phoneNumber']): string | undefined {
		let rawNum = '';
		let typedNumber = '';
		if (phoneNumber) {
			if (phoneNumber?.rawPhoneNumber) {
				rawNum = phoneNumber.rawPhoneNumber;
			}
			if (rawNum.startsWith('***')) {
				// masked phone number
				return rawNum;
			} else {
				if (phoneNumber?.countryCode) {
					switch (phoneNumber.countryCode.toUpperCase()) {
						case 'US':
							typedNumber = new AsYouType('US').input(rawNum);
							break;
						case 'GB':
							typedNumber = new AsYouType('GB').input(rawNum);
							break;
						case 'NL':
							typedNumber = new AsYouType('NL').input(rawNum);
							break;
						case 'IN':
							typedNumber = new AsYouType('IN').input(rawNum);
							break;
						case 'FR':
							typedNumber = new AsYouType('FR').input(rawNum);
							break;
						case 'NG':
							typedNumber = new AsYouType('NG').input(rawNum);
							break;
						case 'UK':
							typedNumber = new AsYouType('GB').input(rawNum);
							break;
						case 'AU':
							typedNumber = new AsYouType('AU').input(rawNum);
							break;
						case 'BE':
							typedNumber = new AsYouType('BE').input(rawNum);
							break;
						case 'BG':
							typedNumber = new AsYouType('BG').input(rawNum);
							break;
						case 'HR':
							typedNumber = new AsYouType('HR').input(rawNum);
							break;
						case 'CY':
							typedNumber = new AsYouType('CY').input(rawNum);
							break;
						case 'CZ':
							typedNumber = new AsYouType('CZ').input(rawNum);
							break;
						case 'DK':
							typedNumber = new AsYouType('DK').input(rawNum);
							break;
						case 'EE':
							typedNumber = new AsYouType('EE').input(rawNum);
							break;
						case 'FI':
							typedNumber = new AsYouType('FI').input(rawNum);
							break;
						case 'DE':
							typedNumber = new AsYouType('DE').input(rawNum);
							break;
						case 'GR':
							typedNumber = new AsYouType('GR').input(rawNum);
							break;
						case 'HU':
							typedNumber = new AsYouType('HU').input(rawNum);
							break;
						case 'IE':
							typedNumber = new AsYouType('IE').input(rawNum);
							break;
						case 'IT':
							typedNumber = new AsYouType('IT').input(rawNum);
							break;
						case 'LV':
							typedNumber = new AsYouType('LV').input(rawNum);
							break;
						case 'LT':
							typedNumber = new AsYouType('LT').input(rawNum);
							break;
						case 'LU':
							typedNumber = new AsYouType('LU').input(rawNum);
							break;
						case 'MT':
							typedNumber = new AsYouType('MT').input(rawNum);
							break;
						case 'PL':
							typedNumber = new AsYouType('PL').input(rawNum);
							break;
						case 'PT':
							typedNumber = new AsYouType('PT').input(rawNum);
							break;
						case 'RO':
							typedNumber = new AsYouType('RO').input(rawNum);
							break;
						case 'SA':
							typedNumber = new AsYouType('SA').input(rawNum);
							break;
						case 'SK':
							typedNumber = new AsYouType('SK').input(rawNum);
							break;
						case 'SI':
							typedNumber = new AsYouType('SI').input(rawNum);
							break;
						case 'ZA':
							typedNumber = new AsYouType('ZA').input(rawNum);
							break;
						case 'SE':
							typedNumber = new AsYouType('SE').input(rawNum);
							break;
						case 'CH':
							typedNumber = new AsYouType('CH').input(rawNum);
							break;
						case 'TR':
							typedNumber = new AsYouType('TR').input(rawNum);
							break;
						case 'AE':
							typedNumber = new AsYouType('AE').input(rawNum);
							break;
						default:
							typedNumber = rawNum;
							break;
					}

					return typedNumber;
				}
			}
		}
		return phoneNumber?.rawPhoneNumber;
	}
}

@Pipe({ name: 'autoFormatPhoneNumber' })
export class AutoFormatPhoneNumberPipe implements PipeTransform {
	constructor(protected sanitizer: DomSanitizer) {}

	transform(phoneNumber: string | undefined): string | undefined {
		if (!phoneNumber) {
			return undefined;
		}

		let rawNum = phoneNumber.trim();

		// Return masked numbers as-is
		if (rawNum.startsWith('***')) {
			return rawNum;
		}

		// Add + prefix if it's missing and looks like a valid international number
		if (!rawNum.startsWith('+') && /^\d+$/.test(rawNum)) {
			if (rawNum.length >= 10) {
				rawNum = '+' + rawNum;
			}
		}

		try {
			const parsedPhone = parsePhoneNumberFromString(rawNum);
			const country = parsedPhone?.country;

			// Format using AsYouType with detected country
			const formatter = country ? new AsYouType(country) : new AsYouType();
			const formatted = formatter.input(rawNum);

			// Return formatted number if successful, otherwise return original
			return formatted || rawNum;
		} catch (error) {
			// If formatting fails, return the original number
			/* istanbul ignore next: libphonenumber-js handles invalid strings without throwing here. */
			return rawNum;
		}
	}
}

@Pipe({ name: 'deviceControlDays' })
export class DeviceControlDaysPipe implements PipeTransform {
	transform(deviceControl: DeviceControl): string {
		const dayNames: { [key: string]: string } = {
			monday: $localize`:@@deviceControlDays-monday:Monday`,
			tuesday: $localize`:@@deviceControlDays-tuesday:Tuesday`,
			wednesday: $localize`:@@deviceControlDays-wednesday:Wednesday`,
			thursday: $localize`:@@deviceControlDays-thursday:Thursday`,
			friday: $localize`:@@deviceControlDays-friday:Friday`,
			saturday: $localize`:@@deviceControlDays-saturday:Saturday`,
			sunday: $localize`:@@deviceControlDays-sunday:Sunday`,
		};

		let dayLabels = '';
		const days: any = deviceControl.days;
		for (const day of [
			'sunday',
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
		]) {
			if (days[day].value === true) {
				dayLabels +=
					dayNames[day][0].toUpperCase() +
					dayNames[day][1].toLowerCase() +
					', ';
			}
		}

		const dayLabelLength = dayLabels.length;

		// 00:00:00 and 24:00:00 are getting converted to both 24:00 using AccessTimePipe and 00:00 using AccessStartTimePipe
		// hence had to separate out
		const pipeStartDate = new AccessStartTimePipe();
		const pipeEndDate = new AccessTimePipe();
		return `${dayLabels.slice(0, dayLabelLength - 2)} | ${pipeStartDate.transform(deviceControl.startTime)} - ${pipeEndDate.transform(deviceControl.endTime)}`;
	}
}

@Pipe({ name: 'deviceControlDaysOnly' })
export class DeviceControlDaysOnlyPipe implements PipeTransform {
	transform(deviceControl: DeviceControl): string {
		const dayNames: { [key: string]: string } = {
			monday: $localize`:@@deviceControlDays-monday:Monday`,
			tuesday: $localize`:@@deviceControlDays-tuesday:Tuesday`,
			wednesday: $localize`:@@deviceControlDays-wednesday:Wednesday`,
			thursday: $localize`:@@deviceControlDays-thursday:Thursday`,
			friday: $localize`:@@deviceControlDays-friday:Friday`,
			saturday: $localize`:@@deviceControlDays-saturday:Saturday`,
			sunday: $localize`:@@deviceControlDays-sunday:Sunday`,
		};

		let dayLabels = '';
		const days: any = deviceControl.days;
		for (const day of [
			'sunday',
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
		]) {
			if (days[day].value === true) {
				dayLabels +=
					dayNames[day][0].toUpperCase() +
					dayNames[day][1].toLowerCase() +
					', ';
			}
		}

		const dayLabelLength = dayLabels.length;

		const pipeStartDate = new AccessStartTimePipe();
		const pipeEndDate = new AccessTimePipe();
		return dayLabels.slice(0, dayLabelLength - 2);
	}
}

@Pipe({ name: 'deviceControlHoursOnly' })
export class DeviceControlHoursOnlyPipe implements PipeTransform {
	transform(deviceControl: DeviceControl): string {
		// 00:00:00 and 24:00:00 are getting converted to both 24:00 using AccessTimePipe and 00:00 using AccessStartTimePipe
		// hence had to separate out
		const pipeStartDate = new AccessStartTimePipe();
		const pipeEndDate = new AccessTimePipe();

		// Remove leading 0's
		let startTime = pipeStartDate.transform(deviceControl.startTime);
		startTime = startTime?.startsWith('0') ? startTime.slice(1) : startTime;
		let endTime = pipeEndDate.transform(deviceControl.endTime);
		endTime = endTime?.startsWith('0') ? endTime.slice(1) : endTime;

		return `${startTime} - ${endTime}`;
	}
}

@Pipe({ name: 'deviceAccessControlDuration' })
export class DeviceAccessControlDurationPipe implements PipeTransform {
	transform(deviceAccessControl: ControlAssignment[]): string {
		let endDate = '';
		const pipe = new AccessDatePipe();

		if (deviceAccessControl[0].utcEndDate === undefined) {
			endDate = 'No End';
		} else {
			endDate =
				pipe.transform(deviceAccessControl[0].utcEndDate)?.toString() ?? '';
		}

		return `${pipe.transform(deviceAccessControl[0].utcStartDate)} - ${endDate}`;
	}
}

@Pipe({ name: 'eventTypeFormat' })
export class EventTypeFormatPipe implements PipeTransform {
	transform(
		eventType: string,
		deviceRemovalFlag: boolean = false,
		metadata?: any
	): string {
		let prefix = '';
		if (
			['missingKey', 'lowBattery', 'keyNotDetected'].includes(
				eventType as DeviceAlertType
			)
		) {
			prefix = 'Alert | ';
		} else if (eventType.includes('AutoDismissal')) {
			prefix = $localize`:@@pipes-autoDismissalPrefix:Auto Dismissal` + ' | ';
			if (eventType.includes('LowBattery')) {
				eventType = $localize`:@@pipes-lowBatteryEvent:Low Battery`;
			} else {
				eventType = eventType.replace('AutoDismissal', '');
			}
		} else if (eventType.includes('Dismissal')) {
			prefix = 'Manual Dismissal | ';
			eventType = eventType.replace('Dismissal', '');
		} else if (eventType.includes('app')) {
			prefix = 'Access | ';
		} else if (
			eventType.includes('flexCodeAccess') ||
			eventType.includes('oneDayCodeAccess')
		) {
			prefix = 'Access | ';
			eventType = eventType
				.replace('flexCodeAccess', 'Web Code')
				.replace('oneDayCodeAccess', 'Web Code');
		} else if (eventType.includes('macCode')) {
			prefix = 'Access | ';
			eventType = eventType.replace('macCode', 'App Code');
		} else if (eventType.includes('haspCodeAccess')) {
			prefix = 'Hasp | ';
			eventType = eventType.replace('haspCodeAccess', 'Web Code');
		} else if (eventType.includes('deviceLocationNameChangeOnly')) {
			prefix = 'Manual Update | ';
			eventType = eventType.replace(
				'deviceLocationNameChangeOnly',
				'Device Location'
			);
		} else if (
			eventType.includes('requestDeviceRemoval') &&
			!eventType.includes('cancel')
		) {
			eventType = eventType.replace(
				'requestDeviceRemoval',
				deviceRemovalFlag
					? $localize`:@@pipes-requestDeviceRemovalKeepAccess:Request Device Removal: Keep Access`
					: $localize`:@@pipes-requestDeviceRemoval:Request Device Removal`
			);
		} else if (eventType.includes('cancelRequestDeviceRemoval')) {
			eventType = eventType.replace(
				'cancelRequestDeviceRemoval',
				$localize`:@@pipes-removalCancelled:Removal Cancelled`
			);
		} else if (eventType.includes('requestEndAllAccesses')) {
			eventType = eventType.replace(
				'requestEndAllAccesses',
				$localize`:@@pipes-requestDeviceRemovalEndAccess:Request Device Removal: End Access`
			);
		} else if (eventType.includes('requestTransferAccesses')) {
			const baseText = $localize`:@@pipes-requestDeviceRemovalTransferAccess:Request Device Removal: Transfer Access`;
			if (metadata?.sourceSerialNumber && metadata?.targetSerialNumber) {
				eventType = eventType.replace(
					'requestTransferAccesses',
					`${baseText} | ${metadata.sourceSerialNumber} → ${metadata.targetSerialNumber}`
				);
			} else {
				eventType = eventType.replace('requestTransferAccesses', baseText);
			}
		} else if (eventType.includes('doorManualUnlock')) {
			prefix = 'Access | ';
			eventType = eventType.replace(
				'doorManualUnlock',
				'Door Unlock (key override)'
			);
		} else if (eventType.includes('doorButtonLock')) {
			prefix = 'Access | ';
			eventType = eventType.replace('doorButtonLock', 'Manual Lock');
		} else if (eventType.includes('doorManualLock')) {
			prefix = 'Access | ';
			eventType = eventType.replace(
				'doorManualLock',
				'Door Lock (key override)'
			);
		} else if (eventType.includes('doorAutoRelock')) {
			prefix = 'Access | ';
			eventType = eventType.replace('doorAutoRelock', 'Door Auto Re-lock');
		}

		const formattedEventType = eventType
			.replace(/(^|[_-])([a-z])/g, (a, b, c) => c.toUpperCase())
			.replace(/([a-z])([A-Z])/g, (a, b, c) => `${b} ${c}`);

		return prefix + formattedEventType;
	}
}

@Pipe({ name: 'alertReportFormat' })
export class AlertReportFormatPipe implements PipeTransform {
	transform(eventType: string): string {
		let prefix = '';
		if (eventType.includes('AutoDismissal')) {
			prefix = $localize`:@@pipes-autoDismissalPrefix:Auto Dismissal` + ' | ';
		} else if (eventType.includes('Dismissal')) {
			prefix =
				$localize`:@@pipes-manualDismissalPrefix:Manual Dismissal` + ' | ';
		}

		if (eventType.includes('lowBattery')) {
			eventType = $localize`:@@pipes-lowBatteryEvent:Low Battery`;
		} else if (eventType.includes('missingKey')) {
			eventType = $localize`:@@pipes-missingKeyEvent:Missing Key`;
		} else if (eventType.includes('keyNotDetected')) {
			eventType = $localize`:@@pipes-keyNotDetectedEvent:Key Not Detected`;
		} else if (eventType.includes('keyDetected')) {
			eventType = $localize`:@@pipes-keyDetectedEvent:Key Detected`;
		}

		const formattedEventType = eventType
			.replace(/(^|[_-])([a-z])/g, (a, b, c) => c.toUpperCase())
			.replace(/([a-z])([A-Z])/g, (a, b, c) => `${b} ${c}`);

		return prefix + formattedEventType;
	}
}

@Pipe({ name: 'accessTypeFormat' })
export class AccessTypeFormatPipe implements PipeTransform {
	transform(eventType: string): string {
		let prefix = '';
		const accessPrefix = $localize`:@@pipes-accessPrefix:Access` + ' | ';
		if (eventType.includes('doorManualUnlock')) {
			prefix = accessPrefix;
			eventType = $localize`:@@pipes-accessDoorUnlockKeyOverride:Door Unlock (key override)`;
		} else if (eventType.includes('doorButtonLock')) {
			prefix = accessPrefix;
			eventType = $localize`:@@pipes-accessManualLock:Manual Lock`;
		} else if (eventType.includes('doorManualLock')) {
			prefix = accessPrefix;
			eventType = $localize`:@@pipes-accessDoorLockKeyOverride:Door Lock (key override)`;
		} else if (eventType.includes('doorAutoRelock')) {
			prefix = accessPrefix;
			eventType = $localize`:@@pipes-accessDoorAutoRelock:Door Auto Re-lock`;
		} else if (eventType.includes('macCode')) {
			prefix = accessPrefix;
			eventType = $localize`:@@pipes-accessAppCode:App Code`;
		} else if (
			eventType.includes('webCode') ||
			eventType.includes('oneDayCode')
		) {
			prefix = accessPrefix;
			eventType = $localize`:@@pipes-accessWebCode:Web Code`;
		} else if (eventType.includes('ble')) {
			prefix = accessPrefix;
			eventType = $localize`:@@pipes-accessApp:App`;
		}

		const formattedEventType = eventType
			.replace(/(^|[_-])([a-z])/g, (a, b, c) => c.toUpperCase())
			.replace(/([a-z])([A-Z])/g, (a, b, c) => `${b} ${c}`);

		return prefix + formattedEventType;
	}
}

@Pipe({ name: 'keyDetectionStatusFormat' })
export class KeyDetectionStatusFormatPipe implements PipeTransform {
	transform(eventType: string): string {
		const formattedEventType = eventType
			.replace(/(^|[_-])([a-z])/g, (a, b, c) => c.toUpperCase())
			.replace(/([a-z])([A-Z])/g, (a, b, c) => `${b} ${c}`);

		return formattedEventType;
	}
}

@Pipe({ name: 'endVisitTypeFormat' })
export class EndVisitTypeFormatPipe implements PipeTransform {
	transform(endVisitType: string): string {
		if (endVisitType === 'geofenceclose') {
			return 'endActiveVisitByGeofence';
		} else {
			return endVisitType;
		}
	}
}

@Pipe({ name: 'flexCodeFormat' })
export class FlexCodeFormatPipe implements PipeTransform {
	transform(code: string, codeType: string): string {
		if (codeType == 'FlexCode') {
			let codeEdit = code.split('');
			codeEdit.splice(5, 0, '-');
			codeEdit.splice(10, 0, '-');
			code = codeEdit.join('');
		}
		return code;
	}
}

@Pipe({ name: 'billingStartDateFilter' })
export class BillingStartDateFilterPipe implements PipeTransform {
	transform(value: any, ...args: any[]): string | undefined {
		if (!value) {
			return value;
		}
		const datePipe = new DatePipe('en-US');
		const formattedDate = datePipe.transform(value, 'MMM dd, yyyy');
		/* istanbul ignore next: DatePipe throws for invalid truthy values instead of returning null. */
		return formattedDate ?? '';
	}
}

@Pipe({ name: 'requestTimeToLocal' })
export class RequestTimeToLocal implements PipeTransform {
	transform(value: any, ...args: any[]): any {
		const date = DateTime.fromISO(value, { zone: 'UTC' });
		if (!date.isValid) {
			return '';
		}
		// Define format for the desired output
		const timeFormat: Intl.DateTimeFormatOptions = {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		};

		// Determine site locale
		const siteLocale = window.location.pathname.split('/')[1];
		const locale = languageList.some((i) => i.code === siteLocale)
			? siteLocale
			: undefined;

		// Set locale and time zone, then format the date
		const localizedDate = date
			.setLocale(locale || 'en')
			.setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

		const formattedTime = localizedDate.toLocaleString(timeFormat);
		/* istanbul ignore next: Luxon provides an offsetNameShort for valid localized dates. */
		const timeZoneAbbreviation =
			localizedDate.offsetNameShort || localizedDate.toFormat('ZZZ');

		// Combine formatted date, time, and time zone
		return `${formattedTime} ${timeZoneAbbreviation}`;
	}
}

@Pipe({ name: 'abbreviatedTimezone' })
export class ShortTimezonePipe implements PipeTransform {
	transform(zone: string, date: Date | string = new Date()): string {
		if (zone === 'UTC') return 'UTC';
		const dt = DateTime.fromJSDate(new Date(date)).setZone(zone);
		return dt.isValid ? dt.offsetNameShort : 'Invalid zone';
	}
}

@Pipe({ name: 'controlAssignmentDuration' })
export class ControlAssignmentDurationPipe implements PipeTransform {
	constructor() {}

	transform(
		control: ControlAssignment,
		localizeInherited: string = 'Inherited',
		localizeTo: string = 'To',
		localizeNone: string = 'None'
	): string {
		if (!control) return '';
		if (control.isSystemAssignment) return localizeInherited;

		const userTimeZone = this.getUserTimeZone();
		const start = control.utcStartDate
			? DateTime.fromISO(control.utcStartDate, { zone: 'utc' }).setZone(
					userTimeZone
				)
			: null;
		const end = control.utcEndDate
			? DateTime.fromISO(control.utcEndDate, { zone: 'utc' }).setZone(
					userTimeZone
				)
			: null;

		const startStr = start ? start.toLocaleString(DateTime.DATE_MED) : '';
		const endStr = end ? end.toLocaleString(DateTime.DATE_MED) : localizeNone;

		return `${startStr} ${localizeTo} ${endStr}`;
	}

	getUserTimeZone(): string {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	}
}

@Pipe({ name: 'auditLogDetails' })
export class AuditLogDetailsPipe implements PipeTransform {
	/**
	 * Format the audit log description by bolding text before and including the colon or pipe
	 * The backend provides translated descriptions like "Device Installed on Property: Main Street" or "Field | Old -> New"
	 * If there's no colon or pipe, the entire text is bolded
	 * Arrows (->) are replaced with HTML arrows styled in action color
	 */
	transform(description: string | undefined): string | null {
		const desc = description?.trim() ?? '';
		if (!desc) return '- - -';

		const colonIndex = desc.indexOf(':');
		const pipeIndex = desc.indexOf('|');

		// Find the first occurrence of either colon or pipe
		let i = -1;
		if (colonIndex !== -1 && pipeIndex !== -1) {
			i = Math.min(colonIndex, pipeIndex);
		} else if (colonIndex !== -1) {
			i = colonIndex;
		} else if (pipeIndex !== -1) {
			i = pipeIndex;
		}

		if (i === -1) {
			// No colon or pipe = bold entire text
			return `<strong>${desc}</strong>`;
		}

		const label = desc.slice(0, i + 1);
		const rest = desc.slice(i + 1);

		// Wrap label in <strong> and replace arrows in rest
		const restWithArrows = this.replaceArrows(rest);
		return `<strong>${label}</strong>${restWithArrows}`;
	}

	/**
	 * Replace the first text arrow (->) with HTML arrow styled in action color
	 */
	private replaceArrows(text: string): string {
		return text.replace(
			/->/,
			'<span class="audit-log-arrow"><span class="arrow-line"></span><span class="arrow-head"></span></span>'
		);
	}
}

/**
 * Pipe to determine if an audit log event should display the formatted chip component
 * Returns true only when BOTH device control snapshot AND control assignment snapshot exist
 */
@Pipe({ name: 'shouldShowFormattedChip' })
export class ShouldShowFormattedChipPipe implements PipeTransform {
	transform(auditLogEntry: any): boolean {
		return !!(
			auditLogEntry?.deviceControlSnapshot?.id &&
			auditLogEntry?.controlAssignmentSnapshot?.id
		);
	}
}

const languageList = [
	{ code: 'en-GB', label: 'English (UK)' },
	{ code: 'en-US', label: 'English (US)' },
	/*{ code: 'en-SA', label: 'English (UAE)' },
  { code: 'en-ZA', label: 'English (South Africa)' },
  { code: 'ar-SA', label: 'عربالعربية (المملكة العربية السعودية)' },
  { code: 'ar-AE', label: 'العربية (الإمارات العربية المتحدة)' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'bg', label: 'български' },
  { code: 'cy', label: 'Cymraeg' },
  { code: 'cs', label: 'čeština' },*/
	{ code: 'da', label: 'Dansk' },
	{ code: 'de', label: 'Deutsch' },
	// { code: 'de-AT', label: 'Deutsch (Österreich)' },
	// { code: 'de-BE', label: 'Deutsch (Belgien)' },
	// { code: 'de-LU', label: 'Deutsch (Luxemburg)' },
	// { code: 'de-CH', label: 'Deutsch (Schweiz)' },
	/*{ code: 'et', label: 'eesti keel' },
  { code: 'el', label: 'Ελληνικά' },
  { code: 'el-CY', label: 'Ελληνικά (Κύπρος)' },*/
	{ code: 'es', label: 'Español' },
	/*{ code: 'fil', label: 'Filipino' },*/
	{ code: 'fr', label: 'Français' },
	// { code: 'fr-BE', label: 'Français (Belgique)' },
	// { code: 'fr-LU', label: 'Français (Luxembourg)' },
	// { code: 'fr-CH', label: 'Français (Suisse)' },
	// { code: 'ga-IE', label: 'Gaeilge' },
	// { code: 'gu', label: 'ગુજરાતી' },
	// { code: 'hi', label: 'हिंदी' },
	// { code: 'hr', label: 'Hrvatski' },
	{ code: 'it', label: 'Italiano' },
	// { code: 'it-CH', label: 'Italiano (Svizzera)' },
	// { code: 'lv-LV', label: 'latviski' },
	// { code: 'lt', label: 'lietuvių' },
	// { code: 'hu-HU', label: 'Magyar' },
	// { code: 'mt', label: 'Malti' },
	// { code: 'nl-BE', label: 'Nederlands (België)' },
	{ code: 'nl-NL', label: 'Nederlands' },
	/*{ code: 'pl', label: 'Polskie' },
  { code: 'pt', label: 'Portugues' },
  { code: 'ro-RO', label: 'Română' },
  { code: 'fi', label: 'Suomalainen' },
  { code: 'sl', label: 'Slovenščina' },
  { code: 'sk', label: 'Slovenský' },*/
	{ code: 'sv', label: 'Svenska' },
	// { code: 'sv-FI', label: 'Svenska (Finland)' },
	/*{ code: 'pa', label: 'ਪੰਜਾਬੀ' },*/
	{ code: 'tr', label: 'Türk' } /*,
  { code: 'ur', label: 'اردو' }*/,
];
